import { Box, useMediaQuery } from "@material-ui/core";
import BigNumber from "bignumber.js";
import { ethers } from "ethers";
import { useEffect, useMemo, useState } from "react";
import InterestRateFactory from "src/assets/abis/poolMonitoring/InterestRateFactory.json";
import { useProviderContext } from "src/ether-utils/context/providerContext";
import { base27 } from "src/hooks/useContractEditPool";
import useNotify from "src/hooks/useNotify";
import { toFixed } from "src/services/utils";
import FigureText from "src/view/PoolMonitoring/Overview/AssetConfigOption/components/FigureText";

function TokenItem({ tokenData }) {
  const { currentProvider } = useProviderContext();
  const matches = useMediaQuery((theme) => theme.breakpoints.only("xs"));
  const [utilizationOptimal, setUtilizationOptimal] = useState(undefined);
  const [slope1, setSlope1] = useState(undefined);
  const [slope2, setSlope2] = useState(undefined);
  const [baseInterestRate, setBaseInterestRate] = useState(undefined);
  const { errorNotify } = useNotify();

  const { symbol, ltv, lt, lb, rf } = useMemo(() => {
    let rawLtv = tokenData[13];
    if (rawLtv) rawLtv = Number(BigNumber(rawLtv._hex).toFixed()) / 10000;
    let rawLt = tokenData[14];
    if (rawLt) rawLt = Number(BigNumber(rawLt._hex).toFixed()) / 10000;
    let rawLb = tokenData[15];
    if (rawLb) rawLb = Number(BigNumber(rawLb._hex).toFixed()) / 10000 - 1;
    let rawRf = tokenData[16];
    if (rawRf) rawRf = Number(BigNumber(rawRf._hex).toFixed()) / 10000;
    return { symbol: tokenData[7], ltv: rawLtv, lt: rawLt, lb: rawLb, rf: rawRf };
  }, [tokenData]);

  async function getData() {
    try {
      const _contract = new ethers.Contract(tokenData[3], InterestRateFactory, currentProvider);
      const _utilizationOptimal = await _contract.OPTIMAL_UTILIZATION_RATE();
      const _slope1 = await _contract.variableRateSlope1();
      const _slope2 = await _contract.variableRateSlope2();
      const _baseInterestRate = await _contract.baseVariableBorrowRate();
      setUtilizationOptimal(BigNumber(_utilizationOptimal._hex).div(base27).toFixed());
      setSlope1(BigNumber(_slope1._hex).div(base27).toFixed());
      setSlope2(BigNumber(_slope2._hex).div(base27).toFixed());
      setBaseInterestRate(BigNumber(_baseInterestRate._hex).div(base27).toFixed());
    } catch (error) {
      console.error(error);
      errorNotify(error.toString());
    }
  }

  useEffect(() => {
    if (currentProvider && tokenData[3]) getData();
  }, [tokenData, currentProvider]);

  return (
    <Box
      display="flex"
      justifyContent="flex-start"
      alignItems="center"
      flexWrap="wrap"
      style={{ borderBottom: "0.25px solid", marginBottom: "1rem" }}
    >
      <FigureText
        sx={{ display: "flex", alignItems: "baseline" }}
        title="Symbol"
        value={symbol}
        mr={matches ? 0 : 8}
        justifyContent={matches ? "space-between" : "flex-start"}
        width={matches ? "100%" : "inherit"}
        style={{ marginBottom: 15 }}
      />
      <FigureText
        sx={{ display: "flex", alignItems: "baseline" }}
        title="Loan to value"
        value={`${toFixed(ltv * 100, 2)}%`}
        mr={matches ? 0 : 8}
        justifyContent={matches ? "space-between" : "flex-start"}
        width={matches ? "100%" : "inherit"}
        style={{ marginBottom: 15 }}
      />
      <FigureText
        sx={{ display: "flex", alignItems: "baseline" }}
        title="Liquidation Threshold"
        value={`${toFixed(lt * 100, 2)}%`}
        mr={matches ? 0 : 8}
        justifyContent={matches ? "space-between" : "flex-start"}
        width={matches ? "100%" : "inherit"}
        style={{ marginBottom: 15 }}
      />
      <FigureText
        sx={{ display: "flex", alignItems: "baseline" }}
        title="Liquidation Bonus"
        value={`${toFixed(lb * 100, 2)}%`}
        mr={matches ? 0 : 8}
        justifyContent={matches ? "space-between" : "flex-start"}
        width={matches ? "100%" : "inherit"}
        style={{ marginBottom: 15 }}
      />
      <FigureText
        sx={{ display: "flex", alignItems: "baseline" }}
        title="Reserve Factor"
        value={`${toFixed(rf * 100, 2)}%`}
        mr={matches ? 0 : 8}
        justifyContent={matches ? "space-between" : "flex-start"}
        width={matches ? "100%" : "inherit"}
        style={{ marginBottom: 15 }}
      />
      {utilizationOptimal && (
        <FigureText
          sx={{ display: "flex", alignItems: "baseline" }}
          title="Utilization Optimal"
          value={`${toFixed(utilizationOptimal * 100, 2)}%`}
          mr={matches ? 0 : 8}
          justifyContent={matches ? "space-between" : "flex-start"}
          width={matches ? "100%" : "inherit"}
          style={{ marginBottom: 15 }}
        />
      )}
      {slope1 && (
        <FigureText
          sx={{ display: "flex", alignItems: "baseline" }}
          title="Slope1"
          value={`${toFixed(slope1 * 100, 2)}%`}
          mr={matches ? 0 : 8}
          justifyContent={matches ? "space-between" : "flex-start"}
          width={matches ? "100%" : "inherit"}
          style={{ marginBottom: 15 }}
        />
      )}
      {slope2 && (
        <FigureText
          sx={{ display: "flex", alignItems: "baseline" }}
          title="Slope2"
          value={`${toFixed(slope2 * 100, 2)}%`}
          mr={matches ? 0 : 8}
          justifyContent={matches ? "space-between" : "flex-start"}
          width={matches ? "100%" : "inherit"}
          style={{ marginBottom: 15 }}
        />
      )}
      {baseInterestRate && (
        <FigureText
          sx={{ display: "flex", alignItems: "baseline" }}
          title="Base Interest Rate"
          value={baseInterestRate}
          mr={matches ? 0 : 8}
          justifyContent={matches ? "space-between" : "flex-start"}
          width={matches ? "100%" : "inherit"}
          style={{ marginBottom: 15 }}
        />
      )}
    </Box>
  );
}

export default function BatchInitReserveItem({ data }) {
  return (
    <>
      {data[0].map((token, index) => {
        return <TokenItem key={index} tokenData={token} />;
      })}
    </>
  );
}
